import React, { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';

interface RegisterFormProps {
  onToggleMode?: () => void;
}

const ds = {
  bg: '#07070f',
  card: '#0d0d1a',
  elevated: '#141428',
  gold: '#d97706',
  goldLight: '#fbbf24',
  goldHover: '#b45309',
  textPrimary: '#d8c8a8',
  textSecondary: '#c0b098',
  textMuted: '#7a6a55',
  textDim: '#3a2a15',
  borderNormal: 'rgba(148,163,184,0.15)',
  borderSubtle: 'rgba(148,163,184,0.25)',
  borderActive: 'rgba(217,119,6,0.35)',
  fontHeading: "'Inter', sans-serif",
  fontLabel: "'Inter', sans-serif",
  fontBody: "'Inter', sans-serif",
};

export const RegisterForm: React.FC<RegisterFormProps> = ({ onToggleMode }) => {
  const { register, loading } = useAuthStore();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [apiError, setApiError] = useState<string>('');
  const [focusedField, setFocusedField] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: [] }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string[]> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = ['First name is required'];
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = ['Last name is required'];
    }
    if (!formData.email.trim()) {
      newErrors.email = ['Email is required'];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = ['Please enter a valid email address'];
    }
    if (!formData.password) {
      newErrors.password = ['Password is required'];
    } else if (formData.password.length < 6) {
      newErrors.password = ['Password must be at least 6 characters'];
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = ['Please confirm your password'];
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = ['Passwords do not match'];
    }
    if (!formData.companyName.trim()) {
      newErrors.companyName = ['Company name is required'];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) {
      return;
    }

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        companyName: formData.companyName
      });
      window.location.href = '/dashboard';
    } catch (error: any) {
      setApiError(error.response?.data?.message || 'Registration failed');
    }
  };

  const inputStyle = (fieldName: string): React.CSSProperties => ({
    width: '100%',
    padding: '10px 14px',
    background: ds.bg,
    border: `1px solid ${errors[fieldName]?.length ? 'rgba(220,50,50,0.5)' : focusedField === fieldName ? ds.borderActive : ds.borderSubtle}`,
    borderRadius: 4,
    color: ds.textSecondary,
    fontFamily: ds.fontBody,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontFamily: ds.fontLabel,
    fontSize: 9,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: ds.textMuted,
    marginBottom: 6,
  };

  const fieldStyle: React.CSSProperties = { marginBottom: 18 };

  return (
    <div style={{ width: '100%', maxWidth: 440, margin: '0 auto' }}>
      <div style={{
        background: ds.card,
        border: '1px solid rgba(148,163,184,0.22)',
        borderRadius: 8,
        padding: 40,
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            fontFamily: ds.fontLabel,
            fontSize: 9,
            letterSpacing: '0.4em',
            textTransform: 'uppercase',
            color: ds.gold,
            marginBottom: 12,
          }}>
            Accounting Suite
          </div>
          <h1 style={{
            fontFamily: ds.fontHeading,
            fontSize: 32,
            fontWeight: 400,
            color: ds.textPrimary,
            margin: 0,
            lineHeight: 1.2,
          }}>
            Create Account
          </h1>
          <p style={{
            fontFamily: ds.fontLabel,
            fontSize: 9,
            letterSpacing: '0.4em',
            textTransform: 'uppercase',
            color: ds.gold,
            marginTop: 10,
          }}>
            Start managing your accounting today
          </p>
        </div>

        {/* Divider */}
        <div style={{
          height: 1,
          background: `linear-gradient(90deg, transparent, ${ds.borderActive}, transparent)`,
          marginBottom: 28,
        }} />

        {/* API Error */}
        {apiError && (
          <div style={{
            marginBottom: 20,
            padding: '12px 16px',
            background: 'rgba(220,50,50,0.08)',
            border: '1px solid rgba(220,50,50,0.25)',
            borderRadius: 4,
          }}>
            <p style={{
              fontFamily: ds.fontBody,
              fontSize: 13,
              color: '#e08080',
              margin: 0,
            }}>
              {apiError}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* First Name */}
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              placeholder="Enter your first name"
              value={formData.firstName}
              onChange={handleChange}
              onFocus={() => setFocusedField('firstName')}
              onBlur={() => setFocusedField('')}
              style={inputStyle('firstName')}
            />
            {errors.firstName?.map((error, index) => (
              <p key={index} style={{ fontFamily: ds.fontBody, fontSize: 11, color: '#e08080', margin: '4px 0 0' }}>
                {error}
              </p>
            ))}
          </div>

          {/* Last Name */}
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              placeholder="Enter your last name"
              value={formData.lastName}
              onChange={handleChange}
              onFocus={() => setFocusedField('lastName')}
              onBlur={() => setFocusedField('')}
              style={inputStyle('lastName')}
            />
            {errors.lastName?.map((error, index) => (
              <p key={index} style={{ fontFamily: ds.fontBody, fontSize: 11, color: '#e08080', margin: '4px 0 0' }}>
                {error}
              </p>
            ))}
          </div>

          {/* Email */}
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="email">Email Address</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField('')}
              style={inputStyle('email')}
            />
            {errors.email?.map((error, index) => (
              <p key={index} style={{ fontFamily: ds.fontBody, fontSize: 11, color: '#e08080', margin: '4px 0 0' }}>
                {error}
              </p>
            ))}
          </div>

          {/* Company Name */}
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="companyName">Company Name</label>
            <input
              id="companyName"
              name="companyName"
              type="text"
              placeholder="Enter your company name"
              value={formData.companyName}
              onChange={handleChange}
              onFocus={() => setFocusedField('companyName')}
              onBlur={() => setFocusedField('')}
              style={inputStyle('companyName')}
            />
            {errors.companyName?.map((error, index) => (
              <p key={index} style={{ fontFamily: ds.fontBody, fontSize: 11, color: '#e08080', margin: '4px 0 0' }}>
                {error}
              </p>
            ))}
          </div>

          {/* Password */}
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField('')}
              style={inputStyle('password')}
            />
            {errors.password?.map((error, index) => (
              <p key={index} style={{ fontFamily: ds.fontBody, fontSize: 11, color: '#e08080', margin: '4px 0 0' }}>
                {error}
              </p>
            ))}
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: 28 }}>
            <label style={labelStyle} htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              onFocus={() => setFocusedField('confirmPassword')}
              onBlur={() => setFocusedField('')}
              style={inputStyle('confirmPassword')}
            />
            {errors.confirmPassword?.map((error, index) => (
              <p key={index} style={{ fontFamily: ds.fontBody, fontSize: 11, color: '#e08080', margin: '4px 0 0' }}>
                {error}
              </p>
            ))}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 24px',
              background: loading
                ? 'rgba(217,119,6,0.25)'
                : `linear-gradient(135deg, ${ds.gold} 0%, ${ds.goldHover} 100%)`,
              border: 'none',
              borderRadius: 4,
              color: '#07070f',
              fontFamily: ds.fontLabel,
              fontSize: 11,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s',
              opacity: loading ? 0.7 : 1,
              marginBottom: 20,
            }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          {/* Toggle Link */}
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={onToggleMode}
              style={{
                background: 'none',
                border: 'none',
                fontFamily: ds.fontBody,
                fontSize: 13,
                color: ds.textMuted,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Already have an account?{' '}
              <span style={{ color: ds.gold, textDecoration: 'underline' }}>Sign In</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
